import RNMapView, { Marker } from 'react-native-maps';
import { Image, Platform } from 'react-native';
import Layout from '../../constants/Layout';
import React from 'react';

// because of web-specific package aliasing, we can't directly access the named export from 'react-native-maps'
const WebMarker = RNMapView.Marker;

const ZINDEX_TOPMOST = 1000; // Arbitrarily chosen "topmost" value to provide a wide range of possible levels.
const ZINDEX_BOTTOMMOST = 0;

/**
 * MemoizedMarker component.
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.pantry - The pantry object.
 * @param {number} props.selectedPantryId - The active pantry id.
 * @param {function} props.showPantryDetails - Function to show pantry details.
 * @param {function} props.selectPantry - Function to select a pantry.
 * @param {function} props.getIcon - Function to get the pantry icon.
 * @param {React.Ref} ref - Ref to be forwarded.
 * @returns {React.Element} - Returns a React element representing a Marker.
 */
const MemoizedMarker = React.memo(
  ({ pantry, selectedPantryId, showPantryDetails, selectPantry, getIcon, markerRefs }) => {
    const isActive = pantry.id === selectedPantryId;
    const onPress = (e) => {
      showPantryDetails(e, pantry);
      if (!Layout.usePantryTabs) {
        selectPantry(pantry.id);
      }
    };
    const icon = getIcon(pantry);

    const getZIndex = () => {
      if (isActive) return ZINDEX_TOPMOST;
      if (pantry.pantry_id && pantry.pantry_id > 0) return ZINDEX_BOTTOMMOST + 1;
      return ZINDEX_BOTTOMMOST;
    };

    const coordinate = {
      latitude: parseFloat(pantry.latitude),
      longitude: parseFloat(pantry.longitude),
    };
    const ref = (marker) => {
      markerRefs[pantry.id] = marker;
    };

    switch (Platform.OS) {
      case 'android':
        // On android we prefer the `icon` attribute over the nested `Image` component because of known rendering
        // performance issues.  See: https://github.com/PlentifulApp/plentiful/issues/1948
        return (
          <Marker
            testID={`map-pin-${pantry.id}`}
            coordinate={coordinate}
            ref={ref}
            title={pantry.name}
            provider="google"
            onPress={onPress}
            pointerEvents="auto"
            tracksViewChanges={false}
            image={icon}
            zIndex={getZIndex()}
          />
        );
      case 'ios':
        // On iOS we use the nested `Image` component because it doesn't suffer from the same performance issues as on
        // Android, and it allows us to scale the image directly to ovecome a known issue with the size being borked
        // after packaging: See:
        //  * https://github.com/PlentifulApp/plentiful/issues/1948, and
        //  * https://github.com/react-native-maps/react-native-maps/issues/4605
        return (
          <Marker
            testID={`map-pin-${pantry.id}`}
            coordinate={coordinate}
            ref={ref}
            title={pantry.name}
            onSelect={onPress}
            pointerEvents="auto"
            zIndex={getZIndex()}
          >
            <Image source={icon} style={{ width: 30, height: 30 }} resizeMode="contain" />
          </Marker>
        );
      default:
        return (
          <WebMarker
            coordinate={coordinate}
            ref={ref}
            title={pantry.name}
            onPress={onPress}
            icon={icon}
            zIndex={getZIndex()}
          />
        );
    }
  }
);

export default MemoizedMarker;
